import { quoteKey, type Asset, type AssetClass, type FxRates, type Quote } from '@/types';

export interface Valuation {
  priceUsd: number | null; // unit price in USD
  valueUsd: number | null;
  costUsd: number | null; // total cost in USD, when cost is known
  quote: Quote | undefined;
}

/** USD per one unit of `currency`, or null when the rate is unknown. */
export function usdPerUnit(currency: string, fx: FxRates | null): number | null {
  const c = currency.toUpperCase();
  if (c === 'USD') return 1;
  const rate = fx?.rates[c];
  return rate && rate > 0 ? 1 / rate : null;
}

export function valueAsset(asset: Asset, quotes: Record<string, Quote | undefined>, fx: FxRates | null): Valuation {
  const key = quoteKey(asset);
  const quote = quotes[key];
  let priceUsd: number | null = null;

  if (asset.assetClass === 'cash') {
    priceUsd = usdPerUnit(asset.currency, fx);
  } else if (quote) {
    priceUsd = quote.currency.toUpperCase() === 'USD' ? quote.price : (usdPerUnit(quote.currency, fx) ?? 0) * quote.price || null;
  } else if (asset.manualPrice) {
    priceUsd = (usdPerUnit(asset.currency, fx) ?? 1) * asset.manualPrice;
  }

  const valueUsd = priceUsd == null ? null : priceUsd * asset.quantity;
  const costUnitUsd = asset.costPrice != null ? (usdPerUnit(asset.costCurrency ?? asset.currency, fx) ?? null) : null;
  const costUsd = asset.costPrice != null && costUnitUsd != null ? asset.costPrice * costUnitUsd * asset.quantity : null;
  return { priceUsd, valueUsd, costUsd, quote };
}

export function convertFromUsd(usd: number, currency: string, fx: FxRates | null): number | null {
  const c = currency.toUpperCase();
  if (c === 'USD') return usd;
  const rate = fx?.rates[c];
  return rate ? usd * rate : null;
}

export interface Totals {
  totalUsd: number;
  byPortfolio: Record<string, number>;
  byClass: Record<AssetClass, number>;
  dayChangeUsd: number;
  dayChangeKnownUsd: number; // value of positions for which a day change is known
  missing: number; // assets without a price
}

export function computeTotals(assets: Asset[], quotes: Record<string, Quote | undefined>, fx: FxRates | null): Totals {
  const t: Totals = { totalUsd: 0, byPortfolio: {}, byClass: { stock: 0, etf: 0, fund: 0, cash: 0, crypto: 0 }, dayChangeUsd: 0, dayChangeKnownUsd: 0, missing: 0 };
  for (const a of assets) {
    const v = valueAsset(a, quotes, fx);
    if (v.valueUsd == null) {
      t.missing++;
      continue;
    }
    t.totalUsd += v.valueUsd;
    t.byPortfolio[a.portfolioId] = (t.byPortfolio[a.portfolioId] ?? 0) + v.valueUsd;
    t.byClass[a.assetClass] += v.valueUsd;
    if (v.quote?.changePct != null && a.assetClass !== 'cash') {
      const prev = v.valueUsd / (1 + v.quote.changePct / 100);
      t.dayChangeUsd += v.valueUsd - prev;
      t.dayChangeKnownUsd += v.valueUsd;
    }
  }
  return t;
}
